import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useLocation } from "@tanstack/react-router";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { useThreadActionMenu } from "~/hooks/useThreadActionMenu";
import { useThreadActions } from "~/hooks/useThreadActions";
import { useNewThreadHandler } from "~/hooks/useHandleNewThread";
import { useSettings } from "~/hooks/useSettings";
import { commitThreadRename } from "~/hooks/commitThreadRename";
import { selectProjectByRef, selectSidebarThreadSummaryByRef, useStore } from "~/store";
import type { BreadcrumbLeafActions } from "./GlobalBreadcrumb";
import { useBreadcrumbLeafActionsStore } from "./breadcrumbLeafActionsStore";

/**
 * Gives the breadcrumb's leaf the thread's action menu, from the thread page.
 *
 * Forge's chat header carries no title — the global breadcrumb owns
 * `project / thread` — so this leaf is Forge's counterpart to t3code's
 * right-clickable header title, and this component is what tells it so.
 *
 * Mounted by the thread route rather than by the bar. Everything here needs
 * providers the bar cannot assume it has: `useThreadActions` reaches for a
 * QueryClient, and the bar renders on surfaces without one. The bar reads the
 * published actions and stays dumb.
 *
 * Renders archive and delete confirmations beside the surface that opens them.
 * A dialog owned by the menu would unmount with the menu and take the pending
 * promise with it.
 */
export function BreadcrumbThreadActions({
  threadRef,
  runtimeAvailable = true,
  runtimeId,
}: {
  readonly threadRef: ScopedThreadRef;
  readonly runtimeAvailable?: boolean;
  readonly runtimeId?: string;
}): ReactElement {
  const pathname = useLocation({ select: (location) => location.pathname });
  const publish = useBreadcrumbLeafActionsStore((store) => store.publish);
  const clear = useBreadcrumbLeafActionsStore((store) => store.clear);

  const [renamingTitle, setRenamingTitle] = useState<string | null>(null);
  const confirmThreadDelete = useSettings((settings) => settings.confirmThreadDelete);
  const { deleteThread, archiveThread, unarchiveThread, settleThread, unsettleThread } =
    useThreadActions();
  const { handleNewThread } = useNewThreadHandler();

  // Primitives only. A string compares equal, so neither subscription can
  // become a re-render source for a component mounted on every thread route.
  const projectCwd = useStore((state) => {
    const thread = selectSidebarThreadSummaryByRef(state, threadRef);
    if (!thread) return null;
    return (
      selectProjectByRef(state, scopeProjectRef(threadRef.environmentId, thread.projectId))?.cwd ??
      null
    );
  });
  const currentTitle = useStore(
    (state) => selectSidebarThreadSummaryByRef(state, threadRef)?.title ?? null,
  );

  const startRename = useCallback((title: string) => setRenamingTitle(title), []);
  const { openThreadActionMenu, DestructiveConfirmDialog } = useThreadActionMenu({
    threadRef,
    runtimeAvailable,
    runtimeId,
    projectCwd,
    confirmThreadDelete,
    deleteThread,
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    handleNewThread,
    startRename,
  });

  const onCommit = useCallback(
    (title: string) => {
      void commitThreadRename({
        threadRef,
        newTitle: title,
        originalTitle: currentTitle ?? "",
        finishRename: () => setRenamingTitle(null),
      });
    },
    [currentTitle, threadRef],
  );
  const onCancel = useCallback(() => setRenamingTitle(null), []);

  // Memoized because this object IS the store's value: rebuilding it every
  // render would republish every render and re-render the bar with it.
  const actions = useMemo<BreadcrumbLeafActions>(
    () => ({
      ...(runtimeAvailable ? { onRename: () => startRename(currentTitle ?? "") } : {}),
      onContextMenu: (position) => void openThreadActionMenu(position),
      ...(renamingTitle === null ? {} : { rename: { value: renamingTitle, onCommit, onCancel } }),
    }),
    [
      currentTitle,
      runtimeAvailable,
      startRename,
      onCancel,
      onCommit,
      openThreadActionMenu,
      renamingTitle,
    ],
  );

  useEffect(() => {
    publish(pathname, actions);
    return () => clear(pathname);
  }, [actions, clear, pathname, publish]);

  return <DestructiveConfirmDialog />;
}
